import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Award, Star } from "lucide-react";
import { toast } from "sonner";

export default function AdminBadges() {
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [role, setRole] = useState("coach");
  const [loading, setLoading] = useState(true);
  const [awarding, setAwarding] = useState(null);

  const load = () => {
    setLoading(true);
    api.get("/admin/badges/users", { params: { role } })
      .then((r) => {
        setStats(r.data.stats);
        setUsers(r.data.users || []);
      })
      .catch(() => toast.error("Erreur chargement badges"))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, [role]);

  const awardPlatformPick = async (coachId) => {
    setAwarding(coachId);
    try {
      await api.post(`/admin/badges/coach/${coachId}/award`, { badge_code: "platform_pick" });
      toast.success("Badge plateforme attribué");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur");
    } finally {
      setAwarding(null);
    }
  };

  return (
    <div data-testid="admin-badges-page">
      <h1 className="font-heading text-2xl font-bold text-slate-900 mb-2">Badges</h1>
      <p className="text-slate-500 mb-6">Adoption et attribution manuelle</p>

      {stats && (
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          {[
            { label: "Badges élèves", value: stats.student_badges_earned },
            { label: "Badges coachs", value: stats.coach_badges_earned },
            { label: "Adoption élèves", value: `${stats.student_adoption_percent}%` },
            { label: "Adoption coachs", value: `${stats.coach_adoption_percent}%` },
          ].map((s) => (
            <Card key={s.label} className="border-slate-100">
              <CardContent className="p-4">
                <p className="text-xs text-slate-400 uppercase tracking-wider">{s.label}</p>
                <p className="text-2xl font-bold text-slate-900 mt-1">{s.value}</p>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <div className="flex gap-2 mb-4">
        {["coach", "student"].map((r) => (
          <Button key={r} variant={role === r ? "default" : "outline"} size="sm" onClick={() => setRole(r)}>
            {r === "coach" ? "Coachs" : "Élèves"}
          </Button>
        ))}
      </div>

      {loading ? (
        <Skeleton className="h-64 w-full rounded-xl" />
      ) : (
        <div className="space-y-2">
          {users.map((u) => (
            <Card key={u.user_id} className="border-slate-100">
              <CardContent className="p-4 flex flex-wrap items-center justify-between gap-3">
                <div>
                  <p className="font-semibold text-slate-900">{u.name}</p>
                  <p className="text-xs text-slate-400">{u.email}</p>
                </div>
                <Badge variant="secondary" className="gap-1">
                  <Award className="w-3.5 h-3.5" />
                  {u.earned_count} / {u.total_count}
                </Badge>
                {role === "coach" && (
                  <Button
                    size="sm"
                    variant="outline"
                    className="gap-1"
                    disabled={awarding === u.user_id}
                    onClick={() => awardPlatformPick(u.user_id)}
                  >
                    <Star className="w-3.5 h-3.5" />
                    Coup de cœur plateforme
                  </Button>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}